import { queryOptions } from '@tanstack/react-query';
import type {
  PhoneTeamSettingsDto,
  PhoneCustomerSettingsDto,
  PhonePromptAssetMetadataDto,
} from '@solidops/shared/phone';
import {
  phoneTeamSettingsUpdateSchema,
  phoneCustomerSettingsUpdateSchema,
  phoneTeamNumberSchema,
  phonePromptAssetMetadataSchema,
} from '@solidops/shared/phone';
import {
  getTeamPhoneSettings,
  getCustomerPhoneSettings,
  getTeamPhoneNumber,
  getGetTeamPhoneSettingsQueryKey,
  getGetCustomerPhoneSettingsQueryKey,
  getGetTeamPhoneNumberQueryKey,
  saveTeamPhoneSettings,
  saveCustomerPhoneSettings,
  uploadPhonePromptAsset,
  previewPhonePromptAsset,
} from './generated/phone/phone.js';
import { ApiError, queryClient } from './client.js';

export type PhoneConfiguration =
  | { kind: 'team'; settings: PhoneTeamSettingsDto }
  | { kind: 'customer'; settings: PhoneCustomerSettingsDto };
export type PhoneOwner = PhoneConfiguration['kind'];
export const phoneSettingsKey = (kind: PhoneOwner, id: string) =>
  kind === 'team' ? getGetTeamPhoneSettingsQueryKey(id) : getGetCustomerPhoneSettingsQueryKey(id);
export { getGetTeamPhoneNumberQueryKey as getTeamPhoneNumberQueryKey };
async function scoped<T>(
  read: () => Promise<T>,
  key: readonly unknown[],
  signal: AbortSignal,
): Promise<T> {
  const previous = queryClient.getQueryCache().findAll({ queryKey: key });
  try {
    const result = await read();
    signal.throwIfAborted();
    return result;
  } catch (error) {
    signal.throwIfAborted();
    if (error instanceof ApiError && [401, 403, 404].includes(error.status))
      for (const query of new Set([
        ...previous,
        ...queryClient.getQueryCache().findAll({ queryKey: key }),
      ])) {
        query.setState({ data: undefined, dataUpdatedAt: 0 });
        if (query.state.fetchStatus !== 'fetching')
          queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
      }
    throw error;
  }
}
export async function readPhoneSettings(
  kind: PhoneOwner,
  id: string,
  signal: AbortSignal,
): Promise<PhoneConfiguration> {
  return scoped(
    async () =>
      kind === 'team'
        ? { kind, settings: await getTeamPhoneSettings(id, { signal }) }
        : { kind, settings: await getCustomerPhoneSettings(id, { signal }) },
    phoneSettingsKey(kind, id),
    signal,
  );
}
export function getPhoneSettingsQueryOptions(kind: PhoneOwner, id: string) {
  return queryOptions({
    queryKey: phoneSettingsKey(kind, id),
    queryFn: ({ signal }) => readPhoneSettings(kind, id, signal),
  });
}
export function getTeamPhoneNumberQueryOptions(id: string) {
  return queryOptions({
    queryKey: getGetTeamPhoneNumberQueryKey(id),
    queryFn: ({ signal }) =>
      scoped(
        async () => phoneTeamNumberSchema.parse(await getTeamPhoneNumber(id, { signal })),
        getGetTeamPhoneNumberQueryKey(id),
        signal,
      ),
  });
}
export async function savePhoneSettings(
  id: string,
  value: PhoneConfiguration,
  csrfToken: string,
  signal: AbortSignal,
): Promise<PhoneConfiguration> {
  const headers = { 'x-csrf-token': csrfToken, 'Idempotency-Key': crypto.randomUUID() };
  return scoped(
    async () => {
      if (value.kind === 'team') {
        const { version, inboundNumber, codeRequired, messages } = value.settings;
        return {
          kind: 'team',
          settings: await saveTeamPhoneSettings(
            id,
            phoneTeamSettingsUpdateSchema.parse({ version, inboundNumber, codeRequired, messages }),
            { headers, signal },
          ),
        };
      }
      const { version, clientCode, recognizedCallerGreeting } = value.settings;
      return {
        kind: 'customer',
        settings: await saveCustomerPhoneSettings(
          id,
          phoneCustomerSettingsUpdateSchema.parse({
            version,
            clientCode,
            recognizedCallerGreeting,
          }),
          { headers, signal },
        ),
      };
    },
    phoneSettingsKey(value.kind, id),
    signal,
  );
}
export async function uploadPhoneMessage(
  id: string,
  kind: PhoneOwner,
  slot: PhonePromptAssetMetadataDto['slot'],
  file: Blob,
  csrfToken: string,
  signal: AbortSignal,
) {
  return scoped(
    async () =>
      phonePromptAssetMetadataSchema.parse(
        await uploadPhonePromptAsset(
          file,
          { ownerType: kind, ownerId: id, slot },
          {
            headers: { 'x-csrf-token': csrfToken, 'Idempotency-Key': crypto.randomUUID() },
            signal,
          },
        ),
      ),
    phoneSettingsKey(kind, id),
    signal,
  );
}
export async function previewPhoneMessage(assetId: string, signal: AbortSignal): Promise<Blob> {
  const result = await previewPhonePromptAsset(assetId, { signal });
  signal.throwIfAborted();
  return result;
}
